import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

export default function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative w-full h-screen overflow-hidden"
    >
      {/* Video Background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://storage.helloreaddy.io/project_files/1fff4f2a-b372-4b40-8698-6f714b3b8587/2dac046c-8077-4acb-94ea-566602476b58_compressed_Captura-de-tela-2026-07-30-171728.webp"
          className="w-full h-full object-cover object-center"
        >
          {/* Substitua o src abaixo pelo link do seu vídeo */}
          <source src="https://res.cloudinary.com/nar2q9cc/video/upload/v1785449873/WhatsApp_Video_2026-07-30_at_19.17.30_oj6tom.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* Dark overlays for text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40"></div>

      {/* Hero Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 flex flex-col items-center justify-center h-full w-full px-6 md:px-10 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
          className="mb-6"
        >
          <div className="w-16 md:w-20 h-[1px] bg-gradient-to-r from-transparent via-premium-500 to-transparent mx-auto mb-4"></div>
          <span className="text-premium-400 text-xs md:text-sm tracking-[0.3em] uppercase font-medium">
            Barbearia Premium • Fortaleza
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1], delay: 0.7 }}
          className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.15] max-w-5xl mb-6"
        >
          SUA MELHOR VERSÃO
          <br />
          <span className="text-gradient-gold">COMEÇA AQUI.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1], delay: 1 }}
          className="text-white/60 text-base md:text-lg max-w-2xl mb-10 leading-relaxed font-light"
        >
          Muito mais do que um corte. Um espaço criado exclusivamente para homens que valorizam estilo, tradição e excelência.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1], delay: 1.3 }}
          className="flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            href="https://wa.me/5585999999999?text=Ol%C3%A1!%20Gostaria%20de%20agendar%20um%20hor%C3%A1rio."
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-8 py-3.5 rounded-md bg-premium-600 hover:bg-premium-500 text-white font-medium tracking-wide text-sm transition-all duration-500 overflow-hidden whitespace-nowrap"
          >
            <span className="relative z-10">Agendar Agora</span>
            <div className="absolute inset-0 bg-gradient-to-r from-premium-500 via-premium-400 to-premium-500 bg-[length:200%_100%] animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </a>
          <button
            onClick={() => handleScroll('#sobre')}
            className="group flex items-center gap-2 px-8 py-3.5 rounded-md border border-white/15 hover:border-white/30 text-white/80 hover:text-white font-medium tracking-wide text-sm transition-all duration-300 whitespace-nowrap"
          >
            Conheça a Barbearia
            <i className="ri-arrow-down-line transition-transform duration-300 group-hover:translate-y-1"></i>
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center p-1">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            className="w-1 h-2 rounded-full bg-premium-500"
          ></motion.div>
        </div>
      </motion.div>
    </section>
  );
}